import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeftIcon, ShieldIcon } from 'lucide-react';

import {
  ADMIN_NAV,
  NavRail,
  navItemClasses,
  ShellFrame,
  visibleNavItems,
} from '@/features/app-shell';
import { useCurrentUser, useIsOrgAdmin } from '@/features/identity';
import { EmptyState } from '@/shared/components/EmptyState';
import { AdminShellSkeleton } from './AdminSkeletons';
import { cn } from '@/lib/utils';

import { ENVIRONMENT_CHIP, ENVIRONMENT_LABELS } from '../environment';
import { useOrganization } from '../hooks/useOrganization';
import { NotificationBell } from '@/features/notifications';

export interface AdminShellProps {
  children: ReactNode;
}

/**
 * The console's shell: `ShellFrame` filled with what an organization has (spec 073
 * FR-001, FR-005; spec 074 FR-017 – FR-019).
 *
 * It used to be a frame of its own, and the reason it gave was sound but answered the
 * wrong question: every link in the workspace sidebar interpolates a `workspaceId`, so
 * wrapping organization-wide content in *that* would put the reader inside a workspace
 * they are not in. True — and the fix is to share the frame and change its contents,
 * not to build a second frame. Building one produced a full-width header over a short
 * nav column, no brand block, identity in a different place, a 224px column against
 * 240px, and no responsive collapse at all.
 *
 * What is *not* shared is the navigation, which is the requirement: the console offers
 * three organization destinations and no route into any workspace's files, tasks,
 * workflows or secrets (FR-019, `073` FR-006). Sharing a frame must not smuggle those in,
 * which is why the rail takes its destinations as content.
 */
export function AdminShell({ children }: AdminShellProps) {
  const currentUser = useCurrentUser();
  const isOrgAdmin = useIsOrgAdmin();

  // Deliberately still an early return, unlike the console's pages (spec 079 FR-005).
  // The rule there is that furniture which does not depend on the pending request must
  // render anyway — but this shell's chrome *is* what the request decides: until the
  // identity is known there is no answer to whether this reader gets the console rail or
  // the sentence below. Drawing the rail first and swapping it for a refusal would be a
  // flash of the wrong screen, which is worse than a placeholder.
  if (currentUser.isPending) {
    return <AdminShellSkeleton />;
  }

  // FR-004. The service refuses every one of these routes regardless; this is so the
  // refusal is a sentence rather than an error boundary, for the two people who will
  // ever see it: someone following a shared link, and an administrator in a realm where
  // the role was never created — a case the spec's own edge cases call out as
  // indistinguishable from a bug unless it says so.
  if (!isOrgAdmin) return <NotAnAdministrator />;

  return (
    <ShellFrame
      rail={(collapsed) => <ConsoleRail collapsed={collapsed} />}
      topbar={<ConsoleTopbar />}
    >
      {children}
    </ShellFrame>
  );
}

/**
 * The console's destinations, in the application's rail (spec 167): the same
 * rail as a workspace's, in a different room. Navy with a Turquoise edge in
 * both themes, so an administrator can tell the console from a workspace
 * before reading a word. It wears `dark` to take the dark theme's pairings,
 * as the page band does, and its own background so the dark theme still
 * tells it from a workspace's navy rail.
 */
function ConsoleRail({ collapsed }: { collapsed: boolean }) {
  const sections = visibleNavItems(ADMIN_NAV, {
    isOrgAdmin: true,
    isWorkspaceOwner: false,
  });

  return (
    <NavRail
      collapsed={collapsed}
      context={<OrganizationContext collapsed={collapsed} />}
      className="dark bg-console-rail border-r-0 shadow-[inset_-3px_0_0_var(--color-console-edge)] [--nav-active-line:var(--color-console-edge)]"
    >
      <nav aria-label="Organization" className="flex flex-col gap-0.5">
        {sections.map((section) => (
          <Link
            key={section.to}
            to={section.to}
            activeOptions={{ exact: section.exact ?? false }}
            title={collapsed ? section.label : undefined}
            className={navItemClasses({ collapsed })}
          >
            <section.icon
              aria-hidden="true"
              className="size-4 shrink-0"
              strokeWidth={1.5}
            />
            {collapsed ? (
              <span className="sr-only">{section.label}</span>
            ) : (
              section.label
            )}
          </Link>
        ))}
      </nav>
    </NavRail>
  );
}

/**
 * Which organization, and the way out — the console's answer to the workspace card.
 *
 * Out of the console rather than up a level: the console has no parent inside itself,
 * and a breadcrumb naming one would imply a hierarchy that does not exist. Written as a
 * destination — "workspaces" — not as "back", which means something different depending
 * on how the reader arrived.
 */
function OrganizationContext({ collapsed }: { collapsed: boolean }) {
  const { data } = useOrganization();
  const name = data?.name ?? 'Organization';

  if (collapsed) {
    return (
      <div className="flex justify-center">
        <ShieldIcon
          aria-hidden="true"
          className="text-console-edge size-4"
          strokeWidth={1.5}
        />
        <span className="sr-only">{name} — organization console</span>
      </div>
    );
  }

  return (
    <div className="border-sidebar-border flex flex-col gap-1 rounded-md border px-2.5 py-2">
      <span className="flex items-center gap-2 text-sm font-medium">
        <ShieldIcon
          aria-hidden="true"
          className="text-console-edge size-4 shrink-0"
          strokeWidth={1.5}
        />
        <span className="truncate">{name}</span>
      </span>
      <span className="text-console-edge text-2xs font-semibold tracking-wider uppercase">
        Organization console
      </span>
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground focus-visible:ring-sidebar-ring flex items-center gap-1.5 rounded-sm text-xs transition-colors focus-visible:ring-3 focus-visible:outline-none"
      >
        <ArrowLeftIcon
          aria-hidden="true"
          className="size-3.5 shrink-0"
          strokeWidth={1.5}
        />
        Workspaces
      </Link>
    </div>
  );
}

/**
 * The console's top bar: its scope (spec 167).
 *
 * The workspace's bar carries breadcrumbs and a search field; the console has no
 * depth to describe, so it says which room this is, for which organization, in
 * which environment, and keeps the notifications bell (spec 131) — an
 * administrator is told about spend limits here too. The environment is the
 * label set on General, shown here so a change in Production is never made by
 * someone who thought they were in Staging. The collapse control to its left
 * comes from the frame.
 */
function ConsoleTopbar() {
  const { data } = useOrganization();

  return (
    <>
      <p className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        <ShieldIcon
          aria-hidden="true"
          className="text-console-accent size-4 shrink-0"
          strokeWidth={1.5}
        />
        {/* On a phone the shield and the environment carry it; the words
            stay for screen readers, and the chip keeps its room. */}
        <span className="text-foreground shrink-0 font-medium max-sm:sr-only">
          Organization console
        </span>
        {data ? (
          <>
            <span className="text-muted-foreground truncate max-sm:sr-only">
              <span aria-hidden="true">· </span>
              {data.name}
            </span>
            <span
              className={cn(
                'text-2xs shrink-0 rounded-full border px-2 py-px font-semibold tracking-wider uppercase',
                ENVIRONMENT_CHIP[data.environment],
              )}
            >
              {ENVIRONMENT_LABELS[data.environment]}
            </span>
          </>
        ) : null}
      </p>
      <NotificationBell />
    </>
  );
}

function NotAnAdministrator() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6">
      <EmptyState
        icon={ShieldIcon}
        title="Administrators only"
        description="The organization console is open to organization administrators only. If you should have access, ask whoever manages sign-in for your organization to grant you the role — it is granted in the identity provider, not in this application."
      />
      {/* Foreground and underlined, not Light Blue: small text on the dark
          theme's surfaces fails as Light Blue (WD-04). */}
      <Link
        to="/"
        className="text-foreground focus-visible:ring-ring mt-4 rounded-sm text-sm underline underline-offset-2 hover:text-foreground/80 focus-visible:ring-3 focus-visible:outline-none"
      >
        Back to all workspaces
      </Link>
    </div>
  );
}
